
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

/**
 * Custom hook to handle user redirection after authentication
 * Now enhanced to support context from email links
 */
export const useAuthRedirection = (
  user: User | null,
  loading: boolean,
  processingAuth: boolean,
  isPasswordReset: boolean,
  tokenError: any
) => {
  const [redirecting, setRedirecting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Don't redirect if we're processing auth, have errors, or are in password reset flow
    if (processingAuth || tokenError || isPasswordReset || !user || loading || redirecting) {
      return;
    }

    // Parse return path from various sources
    const locationState = location.state as { returnTo?: string } | null;
    const returnPath = locationState?.returnTo || 
                     new URLSearchParams(window.location.search).get('returnTo');

    // Only attempt redirect when we have a valid user and aren't already redirecting
    if (user && !redirecting) {
      console.log(`Auth redirect triggered, returnPath: ${returnPath}`);
      setRedirecting(true);

      // Get context parameters from URL if they exist
      const searchParams = new URLSearchParams(window.location.search);
      const userContext = searchParams.get('user_context');
      const orgContext = searchParams.get('org_context');
      
      // Handle context parameters if present (used in email links)
      if (userContext || orgContext) {
        console.log(`Found context parameters in URL - user: ${userContext}, org: ${orgContext}`);
        
        // If the user_context doesn't match the current user, we need to handle this special case
        // This is likely from an email link that was supposed to be for a different user
        if (userContext && userContext !== user.id) {
          console.warn(`User context mismatch: link for ${userContext} but logged in as ${user.id}`);
          
          // Store these context parameters so they can be used after proper login
          localStorage.setItem('pendingUserContext', userContext);
          if (orgContext) {
            localStorage.setItem('pendingOrgContext', orgContext);
          }
          
          // Get the current path to preserve it through the auth flow
          const currentPath = window.location.pathname + window.location.search;
          localStorage.setItem('pendingRedirectAfterAuth', currentPath);
          
          // Show warning and redirect to auth page
          alert("You're logged in with a different account than the one this link was sent to. Please log out and log in with the correct account.");
          setRedirecting(false);
          
          // Sign out the current user and redirect to auth
          supabase.auth.signOut({ scope: 'local' }).then(() => {
            navigate('/auth', { replace: true });
          });
          
          return;
        }
      }

      // Default redirect if no specific path
      const targetPath = returnPath || '/settings';
      
      // Generate warning if accessing a sensitive area
      const isSensitivePath = targetPath.includes('/admin') || 
                             targetPath.includes('/settings');
      
      if (isSensitivePath && userContext && userContext !== user.id) {
        console.warn(`Potentially sensitive area access with mismatched user context`);
        // Handle as needed for security considerations
      }
      
      console.log(`Redirecting to ${targetPath} after authentication`);
      
      // Use a small delay to ensure auth state is fully processed
      setTimeout(() => {
        navigate(targetPath, { replace: true });
      }, 500);
    }
  }, [user, loading, processingAuth, isPasswordReset, tokenError, navigate, location, redirecting]);

  return { redirecting };
};
